import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface NavbarProps {
  activeSection: string | null;
  onMenuClick: (sectionId: string) => void;
  onLogoClick: () => void;
}

export default function Navbar({ activeSection, onMenuClick, onLogoClick }: NavbarProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const hasSection = activeSection !== null;
  const showDark = isScrolled || hasSection || isMobileMenuOpen;

  const menuItems = [
    { id: 'problem', label: t('nav_why') },
    { id: 'innovation', label: t('nav_system') },
    { id: 'market', label: t('nav_partnership') },
    { id: 'esg', label: t('nav_esg') },
    { id: 'reference', label: t('nav_resources') },
    { id: 'contact', label: t('nav_contact') },
  ];

  const toggleLang = () => {
    i18n.changeLanguage(currentLang === 'ko' ? 'en' : 'ko');
  };

  const handleMenuItemClick = (id: string) => {
    onMenuClick(id);
    setIsMobileMenuOpen(false);
  };

  const handleLogoClick = () => {
    onLogoClick();
    setIsMobileMenuOpen(false);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        showDark ? 'bg-white shadow-md' : 'bg-transparent'
      }`}
    >
      {/* Desktop & Mobile top bar */}
      <div className="w-full px-4 md:px-8 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <button onClick={handleLogoClick} className="whitespace-nowrap cursor-pointer flex-shrink-0">
            <img
              src="https://static.readdy.ai/image/17b7b4fe0c11dd66d6ca2f4f9437a472/96788b68209b0eb4015e90aa725a9998.png"
              alt="K-Grill183 Logo"
              className="h-8 md:h-10 w-auto object-contain"
              style={{ filter: showDark ? 'none' : 'brightness(0) invert(1)' }}
            />
          </button>

          {/* Desktop menu */}
          <div className="hidden md:flex items-center gap-6 lg:gap-8">
            {menuItems.map((item) => (
              <button
                key={item.id}
                onClick={() => handleMenuItemClick(item.id)}
                className={`text-sm font-medium whitespace-nowrap transition-colors duration-300 cursor-pointer hover:text-teal-500 ${
                  showDark ? 'text-gray-700' : 'text-white'
                } ${activeSection === item.id ? 'text-teal-500 font-bold' : ''}`}
                style={{ fontFamily: 'Noto Sans KR, sans-serif' }}
              >
                {item.label}
              </button>
            ))}
            <button
              onClick={toggleLang}
              className={`flex items-center gap-1 text-sm font-semibold whitespace-nowrap transition-colors duration-300 cursor-pointer px-3 py-1 rounded-full border ${
                showDark
                  ? 'border-gray-300 text-gray-700 hover:border-teal-500 hover:text-teal-500'
                  : 'border-white/60 text-white hover:border-white hover:text-white'
              }`}
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-translate-line text-sm"></i>
              </div>
              {currentLang === 'ko' ? 'EN' : 'KO'}
            </button>
          </div>

          {/* Mobile right side: lang toggle + hamburger */}
          <div className="flex md:hidden items-center gap-3">
            <button
              onClick={toggleLang}
              className={`flex items-center gap-1 text-xs font-semibold whitespace-nowrap transition-colors duration-300 cursor-pointer px-2 py-1 rounded-full border ${
                showDark
                  ? 'border-gray-300 text-gray-700'
                  : 'border-white/60 text-white'
              }`}
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              <i className="ri-translate-line text-xs"></i>
              {currentLang === 'ko' ? 'EN' : 'KO'}
            </button>
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`w-9 h-9 flex items-center justify-center cursor-pointer transition-colors duration-300 ${
                showDark ? 'text-gray-700' : 'text-white'
              }`}
            >
              <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile dropdown menu */}
      <div
        className={`md:hidden overflow-hidden transition-all duration-300 ${
          isMobileMenuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
        } bg-white border-t border-gray-100`}
      >
        <div className="px-4 py-3 flex flex-col gap-1">
          {menuItems.map((item) => (
            <button
              key={item.id}
              onClick={() => handleMenuItemClick(item.id)}
              className={`text-left px-4 py-3 rounded-lg text-sm font-medium transition-colors duration-200 cursor-pointer ${
                activeSection === item.id
                  ? 'bg-teal-50 text-teal-600 font-bold'
                  : 'text-gray-700 hover:bg-gray-50 hover:text-teal-500'
              }`}
              style={{ fontFamily: 'Noto Sans KR, sans-serif' }}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>
    </nav>
  );
}
